import { inject, observer } from 'mobx-react';
import React from 'react';
import MainSectionLayout from '../layout/MainSectionLayout';
import { Instance } from 'mobx-state-tree';
import { ActionCode, Page, TLCode } from '../../common/enums';
import {
  AnchorStore,
  GroupStore,
  ApplicationStore,
  AuthenticationStore,
  BankHolidayDateStore,
  BlackPointStatusStore,
  BlackPointStore,
  DutyStore,
  LocaleStore,
  PharmacyDetailedStore,
  PharmacyStore,
  UiStore,
  DutyAssignmentStore,
  UserStore,
  DutyOffRequestStore,
  SystemStatusStore,
  ModalStore,
  MunicipalityStore,
  SecretariatStore,
} from '../../store';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Tab, Tabs } from '@blueprintjs/core';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import PharmacyForm from './PharmacyForm';
import {
  BankHolidayDate,
  BlackPoint,
  DutyAssignment,
  DutyOffRequest,
  User,
  SystemStatus,
  Pharmacy,
} from '../../store/models';
import PharmacyDutiesCalendar from './PharmacyDutiesCalendar';
import PharmacyMapPanel from './PharmacyMapPanel';
import NeighborsPanel from './NeighborsPanel';
import styled from '@emotion/styled';
import { css } from 'emotion';
import PharmacyDutiesList from './PharmacyDutiesList';
import PharmacyDutyRoster from './PharmacyDutyRoster';
import MessageTab from '../SecretariatDashboard/MessageTab';
import { TranslatedMessage } from '../common/translation';
import ShowDutiesOnMap from '../Duties/ShowDutiesOnMap';
import moment from 'moment';
import { IconNames } from '@blueprintjs/icons';
import Select from 'react-select';
import MultiCalendarsForComparison from '../common/MultiCalendarsForComparison';
import { generateBasicIcon } from '../../common/icons';
import { InputGroup } from '@blueprintjs/core';

interface PharmacyPageProps extends RouteComponentProps<{ pharmacyId: string }> {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  UiStore?: Instance<typeof UiStore>;
  BankHolidayDateStore?: Instance<typeof BankHolidayDateStore>;
  BlackPointStatusStore?: Instance<typeof BlackPointStatusStore>;
  BlackPointStore?: Instance<typeof BlackPointStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  DutyOffRequestStore?: Instance<typeof DutyOffRequestStore>;
  DutyStore?: Instance<typeof DutyStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  GroupStore?: Instance<typeof GroupStore>;
  MunicipalityStore?: Instance<typeof MunicipalityStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  ModalStore?: Instance<typeof ModalStore>;
  UserStore?: Instance<typeof UserStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  pharmacyList?: Array<Instance<typeof Pharmacy>>;
  getPharmacyById: (id: number) => any;
}

interface PharmacyPageState {
  bankHolidayDates: Array<Instance<typeof BankHolidayDate>>;
  blackPointList: Array<Instance<typeof BlackPoint>>;
  dutyAssignments: Array<Instance<typeof DutyAssignment>>;
  dutyOffRequests: Array<Instance<typeof DutyOffRequest>>;
  userForPharmacy: Array<Instance<typeof User>>;
  systemStatusForSecretariat: Array<Instance<typeof SystemStatus>>;
  isLoading: boolean;
  pharmacyId: number;
  secretariatId: number;
  isPharmacy: boolean;
  startDateDutyMap?: string;
  endDateDutyMap?: string;
  nextAcceptableBlackpoint?: any;
  dutyAssignmentsOnMap: Array<Instance<typeof DutyAssignment>>;
  systemStatus?: any;
  inputRef?: Instance<typeof Select>;
  selectedPharmacy?: string;
  selectedTabId?: string;
  selectedPlanningId: string;
}

@inject('ApplicationStore')
@inject('AuthenticationStore')
@inject('BankHolidayDateStore')
@inject('BlackPointStore')
@inject('BlackPointStatusStore')
@inject('DutyAssignmentStore')
@inject('DutyOffRequestStore')
@inject('DutyStore')
@inject('LocaleStore')
@inject('PharmacyDetailedStore')
@inject('PharmacyStore')
@inject('AnchorStore')
@inject('GroupStore')
@inject('MunicipalityStore')
@inject('UserStore')
@inject('SystemStatusStore')
@inject('UiStore')
@inject('ModalStore')
@inject('SecretariatStore')
@observer
class PharmacyPage extends React.Component<PharmacyPageProps & WithTranslateProps, PharmacyPageState> {
  state = {
    bankHolidayDates: [],
    blackPointList: [],
    blackPointList2: [],
    dutyAssignments: [],
    dutyOffRequests: [],
    userForPharmacy: [],
    systemStatusForSecretariat: [],
    isLoading: true,
    pharmacyId: 0,
    secretariatId: 0,
    isPharmacy: false,
    messageList: [],
    startDateDutyMap: moment().format(),
    endDateDutyMap: moment().format(),
    dutyAssignmentsOnMap: [],
    nextAcceptableBlackpoint: null,
    systemStatus: null,
    selectedPharmacy: '',
    selectedTabId: '',
    selectedPlanningId: '',
  };

  _isMounted = false;

  suggestInput;

  searchMap = async (criteria: any) => {
    await this.searchDutiesForMap(criteria);
    return this.state.dutyAssignmentsOnMap; // duties;
  };

  async fetchData() {
    const pharmacyIdParam = this.props.match.params.pharmacyId;
    const pharmacyId = Number(pharmacyIdParam);
    const planningId = -1;

    const {
      UserStore,
      AuthenticationStore,
      ApplicationStore,
      PharmacyDetailedStore,
      PharmacyStore,
      UiStore,
    } = this.props;
    this._isMounted && this.setState({ isLoading: true, pharmacyId });

    let isPharmacy = false;
    if (
      AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE) &&
      pharmacyId !== AuthenticationStore!.pharmacyId
    ) {
      ApplicationStore!.navigate(Page.Unauthorized);
    } else if (AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE)) {
      isPharmacy = true;
    }

    if (pharmacyId > 0) {
      await PharmacyDetailedStore!.ensureLoaded(pharmacyId);
      const pharmacy = PharmacyDetailedStore!.getById(pharmacyId)!;
      UiStore!.setLastSelectedPharmacy(pharmacyId);
      const criteria: any = { role: ActionCode.PHARMACY_USER_CODE.toString() };
      const lastPublished = this.state.systemStatus
        ? moment(this.state.systemStatus![0]['status'], 'DD/MM/YYYY').format('YYYY-MM-DD')
        : '';
      await Promise.all([
        this.searchDuties({
          pharmacyId,
          planningId,
          endDate: isPharmacy && pharmacy.level == 3 ? lastPublished + 'T00:00:00.00' : undefined,
        }),
        this.searchBlackPoints({
          pharmacyId,
        }),
        this.loadDataForCalendar(pharmacyId, pharmacy.secretariatId),
        ,
        PharmacyStore!.loadList(),
        UserStore!.search(criteria),
      ]).then((responses) => {
        this._isMounted &&
          this.setState({
            // @ts-ignore
            userForPharmacy: responses[5],
            dutyAssignments: responses[0],
            blackPointList: responses[1],
            secretariatId: pharmacy.secretariatId ? pharmacy.secretariatId : 0,
            ...responses[2],
            isPharmacy,
            isLoading: false,
          });
      });
    } else {
      const criteria: any = { role: ActionCode.PHARMACY_USER_CODE.toString() };
      const usersPharmacy = await UserStore!.search(criteria);
      // @ts-ignore
      this._isMounted && this.setState({ userForPharmacy: usersPharmacy, isLoading: false });
    }
  }

  async componentDidMount() {
    this._isMounted = true;
    this.props.AnchorStore!.loadList();
    this.props.GroupStore!.loadList();
    if (this.props.MunicipalityStore!.dataList.length === 0) this.props.MunicipalityStore!.loadList();
    this.props.SecretariatStore!.loadList();
    const criteria: any = {
      subSystem: 'lastPublishedDuty',
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    this._isMounted && this.setState({ systemStatus });

    const criteria2: any = {
      subSystem: 'nextAcceptableBlackpoint',
    };
    const nextAcceptableBlackpoint = await this.props.SystemStatusStore!.search(criteria2);
    this._isMounted && this.setState({ nextAcceptableBlackpoint });

    await this.fetchData();
    if (this.suggestInput) {
      this.suggestInput.focus();
    }
    document.addEventListener('keydown', this._handleKeyDown);
    // this.setState({ selectedPlanningId : this.props.UiStore!.diagnosticPageCurrentPlanningId.toString() });
    this._isMounted && this.setState({ selectedPlanningId: '-1' });
    this._isMounted && this.setState({ selectedTabId: this.state.pharmacyId > 0 ? 'dutyCalendar' : 'details' });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _handleKeyDown = (event) => {
    if (this.state.selectedTabId === 'dutyCalendar') {
      switch (event.keyCode) {
        // case 13:
        //     alert('RETURN');
        //     break;
        default:
          // if (this.suggestInput) this.suggestInput.text = this.suggestInput.text + event.keyCode;
          this._isMounted && this.setState({ selectedPharmacy: this.state.selectedPharmacy + event.key });
          break;
      }
    }
  };

  componentDidUpdate(prevProps: PharmacyPageProps) {
    if (prevProps.match.params.pharmacyId !== this.props.match.params.pharmacyId) {
      this.fetchData();
    }
    if (this.suggestInput) this.suggestInput.focus();
  }

  savePharmacy = (data) => {
    this.props.PharmacyDetailedStore!.savePharmacyDetails(data);
  };

  searchBlackPoints = async (criteria: any) => {
    let result;
    await Promise.all([this.props.BlackPointStore!.search(criteria)])
      .catch((e) => console.log(e))
      .then((responses) => (result = responses[0]));

    return result;
  };

  searchDutyOffRequests = async (criteria: any) => {
    let result;
    await Promise.all([this.props.DutyOffRequestStore!.searchDutyOffRequest(criteria)])
      .catch((e) => console.log(e))
      .then((responses) => (result = responses[0]));

    return result;
  };

  searchDuties = async (criteria: any) => {
    const { DutyAssignmentStore, PharmacyStore, DutyStore } = this.props;
    let result;
    await Promise.all([
      DutyAssignmentStore!.searchAssignment(criteria),
      //PharmacyStore!.searchForDutyAssignment(criteria),
      DutyStore!.searchForDutyAssignment(criteria),
    ])
      .catch(console.error)
      .then((responses) => (result = responses[0]));

    return result;
  };

  searchDutiesForMap = async (criteria: any) => {
    const { DutyAssignmentStore, PharmacyStore, DutyStore } = this.props;
    await Promise.all([
      DutyAssignmentStore!.searchAssignment(criteria),
      PharmacyStore!.searchForDutyAssignment(criteria),
      DutyStore!.searchForDutyAssignment(criteria),
    ])
      .catch((e) => console.error(e))
      .then((responses) => {
        this._isMounted && this.setState({ dutyAssignmentsOnMap: responses[0] });
      });
  };

  loadDataForCalendar = async (pharmacyId: number, secretariatId: number | undefined) => {
    let result = {};
    const promises = [
      this.props.BankHolidayDateStore!.loadList(),
      this.props.DutyOffRequestStore!.searchDutyOffRequest({ pharmacyId }),
    ] as Array<Promise<any>>;
    if (secretariatId != null) {
      const criteriaBP: any = { secretariatId };
      promises.push(this.props.SystemStatusStore!.search(criteriaBP));
    } else {
      const criteriaBP: any = { secretariatId: 1 };
      promises.push(this.props.SystemStatusStore!.search(criteriaBP));
    }

    await Promise.all(promises)
      .catch((e) => console.log(e))
      .then((responses) => {
        if (responses) {
          result = Object.assign(result, { bankHolidayDates: responses[0], dutyOffRequests: responses[1] });
          if (responses.length === 3) result = Object.assign(result, { systemStatusForSecretariat: responses[2] });
        }
      });

    return result;
  };

  saveCalendarChange = async (data: any) => {
    await this.props.PharmacyDetailedStore!.saveCalendarChange(data);
    this.searchBlackPoints({ pharmacyId: this.state.pharmacyId }).then(
      (resp) => this._isMounted && this.setState({ blackPointList: resp }),
    );
    this.searchDutyOffRequests({ pharmacyId: this.state.pharmacyId }).then(
      (resp) => this._isMounted && this.setState({ dutyOffRequests: resp }),
    );
    this.searchDuties({ pharmacyId: this.state.pharmacyId }).then(
      (resp) => this._isMounted && this.setState({ dutyAssignments: resp }),
    );
    const dutiesOff: any = await this.props.DutyOffRequestStore!.searchDutyOffRequest({
      pharmacyId: this.state.pharmacyId,
    });
    this._isMounted && this.setState({ dutyOffRequests: dutiesOff });
  };

  onSelectedPharmacyChanged2 = (selectedOption: any) => {
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${selectedOption.value}`);
  };

  onMapDateChange = async (date?: string) => {
    this._isMounted && this.setState({ startDateDutyMap: date, endDateDutyMap: date });
  };

  navigate = (data: any) => {
    console.log('navigatestart' + Date());

    let pharmacyId = this.state.pharmacyId;
    const pharmacy = this.props.PharmacyDetailedStore!.dataList.filter((p) => p.pharmacyId === pharmacyId)[0];
    let subList = [] as any[];
    let minRest = 0;

    if (data === 'NEXT') {
      subList = this.props.PharmacyStore!.dataList.filter((p) => p.apbNumber > pharmacy.apbNumber && p.level === 3);
      minRest = Math.min(...subList!.map((item) => item.apbNumber));
    } else if (data === 'PREVIOUS') {
      subList = this.props.PharmacyStore!.dataList.filter((p) => p.apbNumber < pharmacy.apbNumber && p.level === 3);
      minRest = Math.max(...subList!.map((item) => item.apbNumber));
    } else {
      return;
    }

    const s = subList!.filter((p) => p.apbNumber === minRest)[0];
    pharmacyId = s.pharmacyId;
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${pharmacyId}`);
    console.log('navigateEnd' + Date());
  };

  generateDutyReport = async (criteria: any) => {
    this._isMounted && this.setState({ isLoading: true });
    await Promise.all([
      this.props.DutyAssignmentStore!.dutyRosterReport(criteria, this.restoreLoadingStatus, this.restoreLoadingStatus),
    ])
      .catch(console.error)
      .then((responses) => {});
  };

  restoreLoadingStatus = () => {
    this._isMounted && this.setState({ isLoading: false });
  };

  render() {
    const {
      translate,
      PharmacyDetailedStore,
      PharmacyStore,
      GroupStore,
      MunicipalityStore,
      AuthenticationStore,
      ApplicationStore,
      ModalStore,
      SecretariatStore,
    } = this.props;
    const { systemStatusForSecretariat, secretariatId, isPharmacy } = this.state;

    const { pharmacyId } = this.state;
    const pharmacyDetails = PharmacyDetailedStore!.getByIdOrNew(pharmacyId);

    const pharmacyList = PharmacyStore!.dataList;

    const selectedOption = this.state.selectedPharmacy;

    const result = (
      <MainSectionLayout
        isLoading={this.state.isLoading}
        title={TLCode.PHARMACY_Title}
        renderRightComponent={
          !isPharmacy
            ? () => (
                <DivLine>
                  <Button>
                    <Link to={`${Page.Pharmacies}`}>
                      <TranslatedMessage id={TLCode.BackToList} />
                    </Link>
                  </Button>
                  <Button
                    icon={IconNames.CHEVRON_LEFT}
                    large={true}
                    style={{ marginLeft: '5px' }}
                    onClick={() => this.navigate('PREVIOUS')}
                  />
                  <DivColumnExt>
                    <Select
                      ref={(input) => (this.suggestInput = input)}
                      autoFocus={true}
                      value={selectedOption}
                      onChange={this.onSelectedPharmacyChanged2}
                      options={pharmacyList.map((elem) => {
                        return {
                          value: elem.pharmacyId,
                          label:
                            '(' +
                            elem.level +
                            '- group ' +
                            elem.group +
                            ') - ' +
                            elem.apbNumber +
                            ' ' +
                            elem.name +
                            ' @ ' +
                            elem.city,
                        };
                      })}
                    />
                  </DivColumnExt>
                  <Button
                    icon={IconNames.CHEVRON_RIGHT}
                    large={true}
                    style={{ height: '30px', marginRight: '5px' }}
                    onClick={() => this.navigate('NEXT')}
                  />
                  <InputGroup
                    placeholder="PlanningId"
                    // leftIcon="search"
                    // value={this.state.planningIdFilter}
                    value={this.state.selectedPlanningId}
                    onChange={(e) => {
                      //   this.setState({ selectedPlanningId: e.target.value });
                    }}
                  />
                  <Button
                    icon={IconNames.RESET}
                    large={true}
                    style={{ height: '30px', marginRight: '5px' }}
                    onClick={() => this.setState({ selectedPlanningId: '-1' })}
                  />
                </DivLine>
              )
            : undefined
        }
      >
        <Container>
          <Card className={CardStyle.toString()}>
            <Tabs
              renderActiveTabPanelOnly={true}
              id="main"
              defaultSelectedTabId={pharmacyId > 0 ? 'dutyCalendar' : 'details'}
              selectedTabId={this.state.selectedTabId}
              onChange={(tabId: string) => {
                this.props.UiStore!.setPharmacyPageActiveTab(tabId.toString());
                this.setState({ selectedTabId: tabId });
              }}
            >
              <Tab
                id="details"
                title={translate(TLCode.PHARMACY_Details)}
                panel={
                  <PharmacyForm
                    pharmacy={pharmacyDetails}
                    save={this.savePharmacy}
                    // refreshGeoData={this.updateGeoData}
                    groupList={GroupStore!.dataList}
                    municipalityList={MunicipalityStore!.dataList}
                    userList={this.state.userForPharmacy}
                    secretariatList={SecretariatStore!.dataList}
                    userRoles={AuthenticationStore!.userActionCodes}
                  />
                }
              />
              <Tab
                id="dutyCalendar"
                title={translate(TLCode.PHARMACY_DutyCalendar)}
                disabled={pharmacyId === -1}
                panel={
                  <PharmacyDutiesCalendar
                    bankHolidayDateList={this.state.bankHolidayDates}
                    blackPointList={this.state.blackPointList}
                    dutyAssignments={this.state.dutyAssignments}
                    dutyOffRequests={this.state.dutyOffRequests}
                    pharmacyDetails={pharmacyDetails}
                    saveCalendarChange={this.saveCalendarChange}
                    pharmacyId={this.state.pharmacyId}
                    systemStatus={systemStatusForSecretariat}
                    isSecretariat={
                      this.props.AuthenticationStore!.isSecretariat ||
                      this.props.AuthenticationStore!.isAdmin ||
                      this.props.AuthenticationStore!.isSuperAdmin
                    }
                    inMultiCalendar={false}
                    ModalStore={ModalStore}
                    userList={this.state.userForPharmacy}
                    separateToolbox={true}
                  />
                }
              />
              <Tab
                id="dutyList"
                title={translate(TLCode.PHARMACY_DutyList)}
                disabled={pharmacyId === -1}
                panel={<PharmacyDutiesList duties={this.state.dutyAssignments} pharmacyDetails={pharmacyDetails} />}
              />
              <Tab
                id="dutyRoster"
                title={translate(TLCode.PHARMACY_DutyRoster)}
                disabled={pharmacyId === -1}
                panel={
                  <PharmacyDutyRoster
                    getDutyRosterReport={this.generateDutyReport}
                    pharmacyId={pharmacyId}
                    pharmacyDetails={pharmacyDetails}
                    systemStatus={systemStatusForSecretariat}
                  />
                }
              />
              <Tab
                id="map"
                title={translate(TLCode.PHARMACY_Map)} // voisinage
                disabled={pharmacyId === -1}
                panel={
                  pharmacyId > 0 ? (
                    <PharmacyMapPanel
                      pharmacyDetails={pharmacyDetails}
                      PharmacyDetailedStore={PharmacyDetailedStore!}
                      PharmacyStore={PharmacyStore!}
                    />
                  ) : (
                    <div />
                  )
                }
              />
              <Tab
                id="dutyMap"
                title={translate(TLCode.PHARMACY_DutyMap)} // gardes avec légende
                disabled={pharmacyId === -1}
                panel={
                  pharmacyId > 0 && pharmacyDetails ? (
                    <>
                      <ShowDutiesOnMap
                        searchDuties={this.searchMap}
                        pharmacyList={pharmacyList}
                        getPharmacyById={PharmacyStore!.getById}
                        getPharmacyDetails={PharmacyDetailedStore!.loadElement}
                        date={this.state.startDateDutyMap}
                        anchors={this.props.AnchorStore!.dataList}
                        onDateChange={this.onMapDateChange}
                        isSecretariat={AuthenticationStore!.isSecretariat || AuthenticationStore!.isAdmin}
                        presetPharmacy={pharmacyDetails}
                      />
                      <DivLongLine>
                        <DivColumn60>
                          <DivLongLine>
                            <DivColumn175px>
                              <label className="bp3-label .modifier">
                                garde niv.0&nbsp;
                                <img
                                  src={generateBasicIcon(0, false, false, false, false).url /*mm20Gray*/}
                                  width="16"
                                  height="16"
                                />
                              </label>
                            </DivColumn175px>
                            <DivColumn175px>
                              <label className="bp3-label .modifier">
                                garde niv.1&nbsp;
                                <img
                                  src={generateBasicIcon(1, false, false, false, false).url /*yellow*/}
                                  width="16"
                                  height="16"
                                />
                              </label>
                            </DivColumn175px>
                            <DivColumn175px>
                              <label className="bp3-label .modifier">
                                garde niv.3&nbsp;
                                <img
                                  src={generateBasicIcon(3, false, false, false, false).url /*yellowDot*/}
                                  width="16"
                                  height="16"
                                />
                              </label>
                            </DivColumn175px>
                            <DivColumn250px>
                              <label className="bp3-label .modifier">
                                phie sélect. de garde&nbsp;
                                <img
                                  src={generateBasicIcon(3, false, true, false, false).url /*blue*/}
                                  width="16"
                                  height="16"
                                />
                              </label>
                            </DivColumn250px>
                            <DivColumn250px>
                              <label className="bp3-label .modifier">
                                phie sélect. libre&nbsp;
                                <img
                                  src={generateBasicIcon(3, false, false, true, false).url /*lightblue*/}
                                  width="16"
                                  height="16"
                                />
                              </label>
                            </DivColumn250px>
                            <DivColumn350px>
                              <label className="bp3-label .modifier">
                                garde en zone d'exclusion&nbsp;
                                <img
                                  src={generateBasicIcon(1, false, false, false, true).url /*red*/}
                                  width="16"
                                  height="16"
                                />
                                <img
                                  src={generateBasicIcon(3, false, false, false, true).url /*redDot*/}
                                  width="16"
                                  height="16"
                                />
                              </label>
                            </DivColumn350px>
                          </DivLongLine>
                        </DivColumn60>
                      </DivLongLine>
                    </>
                  ) : (
                    <div />
                  )
                }
              />
              <Tab
                id="neighborhood"
                title={translate(TLCode.PHARMACY_Neighborhood)} // liste du voisinage
                disabled={pharmacyId === -1}
                panel={
                  pharmacyId > 0 ? (
                    <NeighborsPanel
                      pharmacyDetails={pharmacyDetails}
                      getPharmacy={PharmacyDetailedStore!.getById}
                      ApplicationStore={ApplicationStore}
                      isPharmacy={isPharmacy}
                    />
                  ) : (
                    <div />
                  )
                }
              />
              <Tab
                id="messages"
                title={translate(TLCode.MESSAGE_TabTitle)}
                panel={<MessageTab secretariatId={secretariatId} pharmacyId={pharmacyId} />}
              />
              {!this.state.isPharmacy && (
                <Tab
                  id="calendar comparison"
                  title={translate(TLCode.PHARMACY_CalendarComparison)}
                  disabled={pharmacyId === -1}
                  panel={
                    pharmacyId > 0 ? (
                      <MultiCalendarsForComparison startingPharmacy={this.props.match.params.pharmacyId} />
                    ) : (
                      <div />
                    )
                  }
                />
              )}
            </Tabs>
          </Card>
        </Container>
      </MainSectionLayout>
    );

    return result;
  }
}

export default withTranslate(PharmacyPage);

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumnExt = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 400px;
`;

const CardStyle = css({
  height: '100%',
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
  '& .ag-theme-balham': {
    width: '100%',
  },
});
const DivLongLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;
const DivColumn350px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 350px;
`;

const DivColumn250px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 250px;
`;

const DivColumn175px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 150px;
`;
const DivColumn60 = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 60%;
`;
